import { useState } from 'react';
import {
  useJobs,
  useJobHistory,
  useRunJob,
  useEnableJob,
  useDisableJob,
  Job,
  JobRun,
  JobStatus,
} from '@/hooks/useJobs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Clock,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Timer,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';

/**
 * Status badge for job runs
 */
function StatusBadge({ status }: { status: JobStatus | 'success' | 'failure' | 'skipped' }) {
  const config: Record<string, { icon: typeof CheckCircle; className: string; label: string }> = {
    success: { icon: CheckCircle, className: 'bg-green-100 text-green-800', label: 'Success' },
    failure: { icon: XCircle, className: 'bg-red-100 text-red-800', label: 'Failed' },
    running: { icon: RefreshCw, className: 'bg-blue-100 text-blue-800', label: 'Running' },
    skipped: { icon: AlertCircle, className: 'bg-gray-100 text-gray-800', label: 'Skipped' },
  };

  const { icon: Icon, className, label } = config[status] || config.skipped;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
}

/**
 * Format duration in ms to human readable
 */
function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

/**
 * Job card component
 */
function JobCard({
  job,
  onRun,
  onToggle,
  isRunning,
  isToggling,
}: {
  job: Job;
  onRun: () => void;
  onToggle: () => void;
  isRunning: boolean;
  isToggling: boolean;
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRunClick = () => {
    if (showConfirm) {
      onRun();
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
      // Auto-close confirmation after 3 seconds
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  return (
    <Card className={!job.enabled ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{job.name}</span>
              {job.lastResult && <StatusBadge status={job.lastResult} />}
              {!job.enabled && (
                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Disabled</span>
              )}
            </div>
            <div className="text-sm text-muted-foreground mb-2">
              Schedule: <code className="bg-muted px-1 rounded">{job.schedule}</code>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {job.lastRun && (
                <span className="flex items-center gap-1">
                  <Timer className="h-3 w-3" />
                  Last run: {formatRelativeTime(job.lastRun)}
                </span>
              )}
              {job.lastDurationMs && (
                <span>Duration: {formatDuration(job.lastDurationMs)}</span>
              )}
            </div>
            {job.lastError && (
              <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                {job.lastError}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {showConfirm ? (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleRunClick}
                disabled={isRunning || !job.enabled}
              >
                Confirm
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRunClick}
                disabled={isRunning || !job.enabled}
                title="Run Now"
              >
                {isRunning ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggle}
              disabled={isToggling}
              title={job.enabled ? 'Disable' : 'Enable'}
            >
              {isToggling ? (
                <LoadingSpinner className="h-4 w-4" />
              ) : job.enabled ? (
                <ToggleRight className="h-4 w-4 text-green-600" />
              ) : (
                <ToggleLeft className="h-4 w-4 text-gray-400" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Job history row
 */
function HistoryRow({ run }: { run: JobRun }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b last:border-0">
      <div
        className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <StatusBadge status={run.status} />
          <span className="font-medium">{run.jobName}</span>
          <span className="text-xs text-muted-foreground capitalize">
            ({run.triggeredBy})
          </span>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{formatDuration(run.durationMs)}</span>
          <span>{formatRelativeTime(run.startedAt)}</span>
          {run.error && (expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
        </div>
      </div>
      {expanded && run.error && (
        <div className="px-3 pb-3">
          <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
            <div className="font-medium mb-1">Error:</div>
            {run.error}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Jobs page
 */
export default function Jobs() {
  const [historyPage, setHistoryPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [togglingJob, setTogglingJob] = useState<string | null>(null);

  const { data: jobs, isLoading: jobsLoading, refetch: refetchJobs } = useJobs();
  const { data: historyData, isLoading: historyLoading } = useJobHistory({
    status: statusFilter || undefined,
    limit: 20,
    offset: (historyPage - 1) * 20,
  });

  const runJobMutation = useRunJob();
  const enableJobMutation = useEnableJob();
  const disableJobMutation = useDisableJob();

  const handleRunJob = async (jobName: string) => {
    setRunningJob(jobName);
    try {
      await runJobMutation.mutateAsync(jobName);
    } finally {
      setRunningJob(null);
    }
  };

  const handleToggleJob = async (job: Job) => {
    setTogglingJob(job.name);
    try {
      if (job.enabled) {
        await disableJobMutation.mutateAsync(job.name);
      } else {
        await enableJobMutation.mutateAsync(job.name);
      }
    } finally {
      setTogglingJob(null);
    }
  };

  // Group jobs by category
  const jobCategories: Record<string, Job[]> = {
    'Communication': [],
    'GitLab Sync': [],
    'Maintenance': [],
    'Reports': [],
    'Other': [],
  };

  jobs?.forEach((job) => {
    if (job.name.includes('support')) {
      jobCategories['Communication'].push(job);
    } else if (job.name.includes('gitlab') || job.name.includes('sync')) {
      jobCategories['GitLab Sync'].push(job);
    } else if (job.name.includes('cleanup') || job.name.includes('ldap')) {
      jobCategories['Maintenance'].push(job);
    } else if (job.name.includes('report')) {
      jobCategories['Reports'].push(job);
    } else {
      jobCategories['Other'].push(job);
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Scheduled Jobs</h1>
          <p className="text-muted-foreground">
            Manage and monitor background jobs
          </p>
        </div>
        <Button variant="outline" onClick={() => refetchJobs()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{jobs?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Enabled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {jobs?.filter((j) => j.enabled).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Failed (Last Run)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {jobs?.filter((j) => j.lastResult === 'failure').length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recent Runs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{historyData?.pagination.total || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs by Category */}
      {jobsLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(jobCategories).map(([category, categoryJobs]) =>
            categoryJobs.length > 0 ? (
              <div key={category}>
                <h2 className="text-lg font-semibold mb-3">{category}</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {categoryJobs.map((job) => (
                    <JobCard
                      key={job.name}
                      job={job}
                      onRun={() => handleRunJob(job.name)}
                      onToggle={() => handleToggleJob(job)}
                      isRunning={runningJob === job.name}
                      isToggling={togglingJob === job.name}
                    />
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Job History */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Run History</CardTitle>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as JobStatus | '');
              setHistoryPage(1);
            }}
            className="text-sm px-3 py-1 rounded border bg-background"
          >
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failure">Failed</option>
            <option value="running">Running</option>
            <option value="skipped">Skipped</option>
          </select>
        </CardHeader>
        <CardContent className="p-0">
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : !historyData?.data.length ? (
            <div className="text-center py-8 text-muted-foreground">
              No job runs found
            </div>
          ) : (
            <div className="divide-y">
              {historyData.data.map((run) => (
                <HistoryRow key={run._id} run={run} />
              ))}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {historyData && historyData.pagination.totalPages > 1 && (
          <div className="p-4 border-t flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={historyPage === 1}
              onClick={() => setHistoryPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              Page {historyPage} of {historyData.pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={historyPage >= historyData.pagination.totalPages}
              onClick={() => setHistoryPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
